// src/services/contractService.js
export const DOC_TYPES = [
  { id: "contract_draft", label: "Contract Draft", code: "[CONTRACT]", color: "#0078d4" },
  { id: "scope_of_work", label: "Scope of Work", code: "[SOW]", color: "#107c10" },
  { id: "commercials", label: "Commercials", code: "[COMM]", color: "#c9a84c" },
  { id: "executed_contract", label: "Executed Contract", code: "[EXECUTED]", color: "#107c10" },
];

// Mapping from old document type IDs to new consolidated categories
const DOC_TYPE_MAPPING = {
  contract_draft: "contract_draft",
  original_document: "contract_draft",
  executed_contract: "executed_contract",
  addendum: "contract_draft",
  approval: "contract_draft",
  insurance: "contract_draft",
  scope_of_work: "scope_of_work",
  kpi: "scope_of_work",
  completion_cert: "scope_of_work",
  pricing: "commercials",
  receipt: "commercials",
  award_letter: "commercials",
  correspondence: "commercials",
  other: "commercials",
};

// Convert old document type IDs to new consolidated categories
export const normalizeDocType = (oldTypeId) => {
  if (!oldTypeId) return "commercials";
  return DOC_TYPE_MAPPING[oldTypeId] || "commercials";
};

export const CONTRACT_STATUSES = {
  REQUESTED: "Requested",
  IN_REVIEW: "In Review",
  AWAITING_EXECUTION: "Awaiting Execution",
  EXECUTED: "Executed",
  EXPIRED: "Expired",
  DEFERRED: "Deferred",
  CANCELLED: "Cancelled",
  CLOSE_OUT: "Close Out",
};

export const CONTRACT_TYPES = [
  "NDA",
  "CRAF",
  "Consultancy Service Agreement",
  "Logic Goods",
  "Logic Service",
  "Standard Goods",
  "Standard Service",
  "Addendum",
];

export const ENTITIES = [
  "LEKOIL NIGERIA LIMITED",
  "LEKOIL 276 LIMITED",
  "LEKOIL OIL AND GAS INVESTMENT LIMITED",
  "CAMBRIDGE LNG LIMITED",
];

export const ENTITY_CODES = {
  "LEKOIL NIGERIA LIMITED": "LEK/NIG",
  "LEKOIL 276 LIMITED": "LEK/276",
  "LEKOIL OIL AND GAS INVESTMENT LIMITED": "LEK/OTAK",
  "CAMBRIDGE LNG LIMITED": "CLNG",
};

export const generateRefNumber = (entity, year, sequence) => {
  const code = ENTITY_CODES[entity] || "CNT";
  const yr = String(year).slice(-2);
  const seq = String(sequence).padStart(2, "0");
  return `${code}/${yr}-${seq}`;
};

/**
 * Returns the next sequence number for a given entity + year.
 * Reads ContractSequences, increments LastSequence, writes back, returns new value.
 * Creates the row automatically on first use.
 */
export const getNextSequence = async (service, entity, year) => {
  const code = ENTITY_CODES[entity] || "CNT";
  const yearStr = String(year);

  // Fetch ALL ContractSequences records and filter in memory
  // This avoids any OData filter syntax issues
  let allSequences = [];
  try {
    const data = await service.getListItems("ContractSequences");
    allSequences = data?.value || [];
  } catch (e) {
    console.error("Error fetching ContractSequences:", e);
    allSequences = [];
  }

  const existing = allSequences.find((item) => {
    const storedCode = String(item.fields?.Title || "").trim();
    const storedYear = String(item.fields?.Year || "").trim();
    return storedCode == code && storedYear == yearStr;
  });

  if (existing) {
    const next = (parseInt(existing.fields?.LastSequence, 10) || 0) + 1;
    await service.updateListItem("ContractSequences", existing.id, {
      LastSequence: next,
    });
    return next;
  }

  await service.createListItem("ContractSequences", {
    Title: code,
    Year: yearStr,
    LastSequence: 1,
  });
  return 1;
};

// For addenda: appends a letter suffix to the parent ref — LEK/276/26-01(a), (b), (c)…
export const generateAddendumRef = (parentRefNo, existingAddendumCount) => {
  const letter = String.fromCharCode(97 + existingAddendumCount); // 0→a, 1→b …
  return `${parentRefNo}(${letter})`;
};

export const buildFolderPath = (
  entity,
  department,
  year,
  month,
  contractRef,
) => {
  const s = (v) => String(v).replace(/[/\\:*?"<>|]/g, "_");
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const mName = months[(month || 1) - 1];
  return `${s(entity)}/${s(department)}/${year}/${mName}/${s(contractRef)}`;
};

export const getStatusColor = (status) => {
  const map = {
    Requested: "#c47d1a",
    "In Review": "#0078d4",
    "Awaiting Execution": "#ca5010",
    Executed: "#004e8c",
    Expired: "#a4262c",
    Deferred: "#797775",
    Cancelled: "#d13438",
    "Close Out": "#6b7280",
  };
  return map[status] || "#797775";
};

export const buildEmailHTML = (type, data) => {
  const wrap = (content) =>
    `<div style="font-family:Segoe UI,sans-serif;max-width:600px;margin:0 auto;background:#fff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.1)"><div style="background:#0078d4;padding:24px 32px"><h1 style="color:#ffffff;font-size:22px;margin:0">LexCore Contract Management</h1><p style="color:#dbe7fa;font-size:13px;margin:4px 0 0">Automated Notification</p></div><div style="padding:32px">${content}</div><div style="background:#f3f2f1;padding:16px 32px;font-size:12px;color:#797775;text-align:center">Automated message from LexCore CMS. Do not reply.</div></div>`;
  const row = (k, v, bg) =>
    `<tr><td style="padding:8px;${bg ? "background:#f3f2f1;" : ""}color:#797775;width:40%">${k}</td><td style="padding:8px;${bg ? "background:#f3f2f1;" : ""}font-weight:600">${v}</td></tr>`;
  const table = (...rows) =>
    `<table style="width:100%;border-collapse:collapse;margin:20px 0">${rows.join("")}</table>`;
  const btn = (label, url, bg = "#0078d4", fg = "#ffffff") =>
    `<a href="${url}" style="display:inline-block;background:${bg};color:${fg};padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:600;margin-top:8px">${label}</a>`;

  if (type === "newRequest")
    return wrap(
      `<h2 style="color:#0a1628">New Contract Request Received</h2><p>A new request has been submitted and requires your attention.</p>${table(row("Contract Title", data.title, false), row("Reference", data.refNo, true), row("Requested By", data.requestedBy, false), row("Vendor", data.vendor, true), row("Type", data.type, false), row("Entity", data.entity, true))}${btn("View Request →", data.portalUrl)}`,
    );
  if (type === "documentShared")
    return wrap(
      `<h2 style="color:#0a1628">Contract Document Shared With You</h2><p>A contract document has been shared with you for review.</p>${table(row("Contract Title", data.title, false), row("Reference", data.refNo, true), row("Shared By", data.sharedBy, false), row("Notes", data.message || "Please review and provide feedback.", true))}${btn("Open Document →", data.documentUrl, "#0078d4", "#fff")}`,
    );
  if (type === "reviewerShared")
    return wrap(
      `<h2 style="color:#0a1628">Review Requested - Contract Documents Ready</h2><p>You have been invited to review the following contract documents. No sign-in is required.</p>${table(row("Contract Title", data.title, false), row("Reference", data.refNo, true), row("Shared By", data.sharedBy, false), row("Notes", data.message || "Please review the documents and provide feedback.", true))}<div style="background:#e7f3ff;border-left:4px solid #0078d4;padding:12px 16px;margin:16px 0;border-radius:4px;"><div style="font-weight:600;color:#0078d4;margin-bottom:6px;">🔗 Private Review Link</div><div style="color:#333;font-size:13px;">Click the button below to access the documents. This link is unique to you and should not be shared.</div></div>${btn("Start Reviewing →", data.documentUrl, "#0078d4", "#fff")}`,
    );
  if (type === "statusUpdate")
    return wrap(
      `<h2 style="color:#0a1628">Contract Status Updated</h2>${table(row("Contract Title", data.title, false), row("Reference", data.refNo, true), row("Old Status", data.oldStatus, false), row("New Status", `<span style="color:${getStatusColor(data.newStatus)}">${data.newStatus}</span>`, true))}${btn("View Contract →", data.portalUrl)}`,
    );
  if (type === "expiryReminder")
    return wrap(
      `<h2 style="color:#a4262c">Contract Expiry Reminder</h2>${table(row("Contract Title", data.title, false), row("Reference", data.refNo, true), row("Expiry Date", data.expiryDate, false), row("Days Remaining", `<span style="color:#a4262c">${data.daysRemaining} days</span>`, true), row("Vendor", data.vendor, false))}${btn("Review Contract →", data.portalUrl, "#a4262c", "#fff")}`,
    );
  return wrap(`<p>${JSON.stringify(data)}</p>`);
};
